/** @jsxImportSource @emotion/react */
import {
  createStyles,
  Table,
  Icon,
  Typography,
  UtilityButton,
  SearchInput,
} from '@quickbit/qb-design-system';
import { ReactNode, useCallback, useState } from 'react';
import { Header } from '../../../../components';
import { useSession } from '../../../../context/Session';
import { User } from '../../../../types';

function startCase(str: string) {
  return str
    .toLowerCase()
    .replace(/^./, (firstLetter) => firstLetter.toUpperCase());
}

interface Props {
  users: User[];
  setSelectedUser: (user: User | 'new') => void;
}

export const UserSearch = ({ users, setSelectedUser }: Props) => {
  const styles = useStyles();
  const session = useSession();

  const [searchValue, setSearchValue] = useState<string>('');

  const headerLabels: Record<string, string> = {
    userId: 'User ID',
    email: 'Email',
    groups: 'Groups',
    deleted: 'Deleted',
    authenticatorEnabled: 'MFA',
  };

  const rows = users
    .filter(({ email, userId }) => {
      if (searchValue === '') {
        return true;
      }
      return email.includes(searchValue) || userId.includes(searchValue);
    })
    // Put the deleted ones at the bottom.
    .sort((lhs, rhs) => +(lhs.deletedAt ?? '' < (rhs.deletedAt ?? '')))
    .reverse()
    .map((user) => ({
      value: {
        userId: user.userId,
        email: user.deletedAt ? (
          <div css={styles.strikeThrough}>{user.email}</div>
        ) : (
          user.email
        ),
        groups: user.groups.join(', '),
        permissions: user.permissions.join(', '),
        deleted: user.deletedAt ? <Icon name="lock" /> : undefined,
        createdBy: user.createdBy ?? '-',
        authenticatorEnabled: user.authenticatorEnabled ? (
          <Icon name="security" />
        ) : undefined,
      },
      disabled: !!user.deletedAt,
    }));

  const handleSelectUser = useCallback(
    (userId: string | number | undefined | ReactNode) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const user = users.find((r) => r.userId === userId)!;
      setSelectedUser(user);
    },
    [setSelectedUser, users]
  );

  return (
    <div>
      <Header>
        <div css={[styles.default]}>
          <div css={styles.titleContainer}>
            <Typography variant="subtitle">Users</Typography>
          </div>
          <SearchInput
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          {session.authenticated &&
            session.permissions.includes('edit_users') && (
              <UtilityButton
                text="Add user"
                variant="primary"
                icon="plus"
                onClick={() => setSelectedUser('new')}
              />
            )}
        </div>
      </Header>
      <div css={styles.tableWrapper}>
        <Table
          headers={Object.keys(rows[0].value)
            .filter((k) => {
              const excludedKeys = ['permissions', 'createdBy'];
              return !excludedKeys.includes(k);
            })
            .map((k) => ({
              label: headerLabels[k] || startCase(k),
              cellKey: k,
            }))}
          rows={rows}
          onRowClick={(rowValue) =>
            !rowValue.deleted && handleSelectUser(rowValue.userId)
          }
        />
      </div>
    </div>
  );
};

const useStyles = createStyles(({ spacing, shape, palette }) => ({
  default: {
    display: 'grid',
    gap: spacing.xxs,
    alignItems: 'center',
    gridTemplateColumns: '2fr 1fr auto',
    'svg:not(button *)': {
      color: palette.grey.x900,
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  tableWrapper: {
    border: `1px solid ${palette.grey.x100}`,
    borderRadius: shape.borderRadius,
  },
  strikeThrough: {
    textDecoration: 'line-through',
  },
}));
